<template>
	<tr class="clickable-row" @click="onRowClick">
		<td id="in-out-checkbox">
			<PermissionsWrapper
				:showDisabledIfNotVisible="true"
				:permissionIds="[Permissions.DisbursementBatches]"
				:permissionLevel="PermissionLevels.Update">
				<label>
					<input
						type="checkbox"
						v-model="isIn"
						:disabled="isBatchApproved || isProcessing || isToggling"
						@change="toggleAddRemoveAsync"
					/>
					<span class="custom-checkbox"></span>
				</label>
			</PermissionsWrapper>
		</td>
		<td>{{ disbursement?.lenderName }}</td>
		<td>{{ disbursement?.borrowerName }}</td>
		<td>{{ disbursement?.disbursementAmountFormatted }}</td>
		<td>{{ disbursement?.disbursementMethodHumanized }}</td>
		<td>{{ disbursement?.disbursementDateFormatted }}</td>
		<td>{{ disbursement?.referenceId }}</td>
		<td>{{ disbursement?.lenderUseOnly }}</td>
		<td>{{ disbursement?.sequenceNumber }}</td>
	</tr>
</template>

<script setup lang="ts">
	import { ref } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { DisbursementBatchReferenceModel, DisbursementDetailsModel } from '../../models/disbursementBatchesV2';

	const props = defineProps<{
		batchId?: number;
		reference: DisbursementBatchReferenceModel;
		disbursement?: DisbursementDetailsModel;
		isBatchApproved?: boolean;
		isProcessing?: boolean;
		onBatchUpdateAsync: () => Promise<void>;
	}>();

	const isIn = ref(!props.reference.isDeleted);
	const isToggling = ref(false);

	const toggleAddRemoveAsync = async () => {
		if (props.isBatchApproved || props.isProcessing || isToggling.value) {
			return;
		}

		isToggling.value = true;

		try {
			await updateReferenceAsync(isIn.value ? 'add' : 'remove');
		} catch (error) {
			console.error(error);
		} finally {
			isToggling.value = false;
		}
	};

	const updateReferenceAsync = async (action: 'add' | 'remove') => {
		try {
			const url = `/api/v2/disbursement-batches/${props.batchId}/disbursement/${props.reference.disbursementId}`;

			if (action === 'add') {
				await axios.post(url);
			} else if (action === 'remove') {
				await axios.delete(url);
			}
			
			await props.onBatchUpdateAsync();
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to ${action} the disbursement ${action === 'add' ? 'to' : 'from'} the batch.`
			});

			isIn.value = !isIn.value; // reset
		}
	};

	const onRowClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement;
		
		if (target.closest("#in-out-checkbox")) {
    		return;
  		}

		window.open(`/certifications/${props.disbursement?.certificationId}`, "_blank");
	}
</script>

<style scoped>
	td {
		padding: 10px 5px;
	}

	input[type='checkbox'] {
		position: absolute;
		opacity: 0;
		cursor: pointer;
	}

	.custom-checkbox {
		display: inline-block;
		width: 25px;
		height: 25px;
		background-color: white;
		border: 2px solid #ccc;
		border-radius: 4px;
		position: relative;
		transition: background-color 0.2s, opacity 0.2s;
	}

	.custom-checkbox:after {
		content: '';
		position: absolute;
		display: none;
		left: 7px;
		top: 3px;
		width: 6px;
		height: 12px;
		border: solid white;
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
	}
	
	.clickable-row:hover {
		background-color: #d4d6dd;
	}

	input[type='checkbox']:checked + .custom-checkbox {
		background-color: black;
		border: 2px solid black;
	}

	input[type='checkbox']:checked + .custom-checkbox:after {
		display: block;
	}

	.custom-checkbox:hover {
		background-color: #ccc;
	}

	input[type='checkbox']:checked + .custom-checkbox:hover {
		background-color: #444;
	}

	input[type='checkbox']:disabled:checked + .custom-checkbox {
		background-color: #ccc;
		border: 2px solid #ccc;
	}

	input[type='checkbox']:disabled + .custom-checkbox {
		background-color: #ccc;
		opacity: 0.7;
		cursor: not-allowed;
	}

	input[type='checkbox']:disabled + .custom-checkbox:hover {
		background-color: #ccc;
	}
</style>
